import React, { useState, useMemo, useEffect } from "react";
import {
    useGetInvestmentsQuery,
    useDirectWithdrawMutation,
    useGetInvestmentStrategyQuery,
    useUpdateInvestmentStrategyMutation,
    useGetStrategiesQuery,
    InvestmentListItem
} from "../api";
import { UserAutocomplete } from "../components/UserAutocomplete";

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

// STRATEGY_NAMES removed, using data from backend

type SortField = "user_id" | "total" | "money_active" | "money_pending" | "available" | "earned" | "withdraw" | "total_earned";
type SortDirection = "asc" | "desc";

export const InvestmentsPage: React.FC = () => {
    const [selectedStrategy, setSelectedStrategy] = useState<number>(0);
    const [userId, setUserId] = useState<string>("");
    const [sortBy, setSortBy] = useState<SortField>("total");
    const [sortOrder, setSortOrder] = useState<SortDirection>("desc");
    const [withdrawingUsers, setWithdrawingUsers] = useState<Set<string>>(new Set());
    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
    const [selectedInvestment, setSelectedInvestment] = useState<InvestmentListItem | null>(null);
    const [withdrawAmount, setWithdrawAmount] = useState<string>("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingInvestment, setEditingInvestment] = useState<InvestmentListItem | null>(null);
    const [editFormData, setEditFormData] = useState({
        money_active: "",
        money_pending: "",
        money_withdraw_pending: "",
        money_transfer_pending: "",
        total: "",
        withdraw: "",
        earned: "",
    });

    const {
        data: investmentsResponse,
        isLoading,
        refetch,
    } = useGetInvestmentsQuery({
        strategy: selectedStrategy,
        user_id: userId ? parseInt(userId) : undefined,
        // Передаем параметры сортировки только для колонок, поддерживаемых бекендом
        sort_by: (sortBy === "total" || sortBy === "available") ? (sortBy as "total" | "available") : undefined,
        sort_order: (sortBy === "total" || sortBy === "available") ? sortOrder : undefined,
    });

    const { data: strategiesResponse } = useGetStrategiesQuery();
    const strategies = strategiesResponse?.data || [];

    const getStrategyName = (id: number) => {
        const strategy = strategies.find((s) => s.id === id);
        return strategy ? `${id}: ${strategy.name}` : `${id}: Стратегия ${id}`;
    };

    const [directWithdraw, { isLoading: isWithdrawing }] = useDirectWithdrawMutation();
    const [updateInvestmentStrategy, { isLoading: isUpdating }] = useUpdateInvestmentStrategyMutation();

    const { data: strategyData, refetch: refetchStrategy } = useGetInvestmentStrategyQuery(
        {
            user_id: editingInvestment?.user_id || 0,
            strategy: editingInvestment?.strategy || 0,
        },
        {
            skip: !editingInvestment || !isEditModalOpen,
        }
    );

    const investmentsData = investmentsResponse?.data || [];
    const meta = investmentsResponse?.meta;

    // Клиентская сортировка для колонок, которые не поддерживаются бекендом
    const investments = useMemo(() => {
        if (sortBy === "total" || sortBy === "available") {
            // Эти колонки сортируются на бекенде
            return investmentsData;
        }

        const sorted = [...investmentsData];
        sorted.sort((a, b) => {
            let valueA: number;
            let valueB: number;

            switch (sortBy) {
                case "user_id":
                    valueA = a.user_id;
                    valueB = b.user_id;
                    break;
                case "money_active":
                    valueA = a.money_active;
                    valueB = b.money_active;
                    break;
                case "money_pending":
                    valueA = a.money_pending;
                    valueB = b.money_pending;
                    break;
                case "earned":
                    valueA = a.earned;
                    valueB = b.earned;
                    break;
                case "withdraw":
                    valueA = a.withdraw;
                    valueB = b.withdraw;
                    break;
                case "total_earned":
                    valueA = a.total_earned;
                    valueB = b.total_earned;
                    break;
                default:
                    return 0;
            }

            if (sortOrder === "asc") {
                return valueA - valueB;
            } else {
                return valueB - valueA;
            }
        });

        return sorted;
    }, [investmentsData, sortBy, sortOrder]);

    const handleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
    };

    const handleStrategyChange = (strategy: number) => {
        setSelectedStrategy(strategy);
    };

    const handleUserIdChange = (value: string) => {
        setUserId(value);
    };

    const handleUserSelect = (user: { id: number }) => {
        setUserId(user.id.toString());
    };

    const handleWithdrawClick = (investment: InvestmentListItem) => {
        setSelectedInvestment(investment);
        setWithdrawAmount(investment.available_for_withdraw.toString());
        setIsWithdrawModalOpen(true);
    };

    const handleWithdrawModalClose = () => {
        setIsWithdrawModalOpen(false);
        setSelectedInvestment(null);
        setWithdrawAmount("");
    };

    const handleWithdrawSubmit = async () => {
        if (!selectedInvestment) {
            return;
        }

        const amount = parseFloat(withdrawAmount);
        if (isNaN(amount) || amount <= 0) {
            window.alert("Введите корректную сумму");
            return;
        }

        if (amount > selectedInvestment.available_for_withdraw) {
            window.alert(`Сумма не может превышать доступную для вывода: ${formatCurrency(selectedInvestment.available_for_withdraw)}`);
            return;
        }

        const key = `${selectedInvestment.user_id}-${selectedInvestment.strategy}`;
        setWithdrawingUsers((prev) => new Set(prev).add(key));

        try {
            await directWithdraw({
                user_id: selectedInvestment.user_id,
                strategy: selectedInvestment.strategy,
                amount: amount,
            }).unwrap();

            window.alert("Вывод средств выполнен успешно");
            handleWithdrawModalClose();
            refetch();
        } catch (error: any) {
            window.alert(
                error?.data?.message ||
                error?.message ||
                "Ошибка при выводе средств"
            );
        } finally {
            setWithdrawingUsers((prev) => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    const handleEditClick = (investment: InvestmentListItem) => {
        setEditingInvestment(investment);
        setIsEditModalOpen(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setEditingInvestment(null);
        setEditFormData({
            money_active: "",
            money_pending: "",
            money_withdraw_pending: "",
            money_transfer_pending: "",
            total: "",
            withdraw: "",
            earned: "",
        });
    };

    useEffect(() => {
        if (strategyData?.data && isEditModalOpen) {
            setEditFormData({
                money_active: strategyData.data.money_active.toString(),
                money_pending: strategyData.data.money_pending.toString(),
                money_withdraw_pending: strategyData.data.money_withdraw_pending.toString(),
                money_transfer_pending: strategyData.data.money_transfer_pending || "",
                total: strategyData.data.total.toString(),
                withdraw: strategyData.data.withdraw.toString(),
                earned: strategyData.data.earned.toString(),
            });
        }
    }, [strategyData, isEditModalOpen]);

    const handleEditSubmit = async () => {
        if (!editingInvestment) {
            return;
        }

        try {
            await updateInvestmentStrategy({
                user_id: editingInvestment.user_id,
                strategy: editingInvestment.strategy,
                money_active: editFormData.money_active ? parseFloat(editFormData.money_active) : undefined,
                money_pending: editFormData.money_pending ? parseFloat(editFormData.money_pending) : undefined,
                money_withdraw_pending: editFormData.money_withdraw_pending ? parseFloat(editFormData.money_withdraw_pending) : undefined,
                money_transfer_pending: editFormData.money_transfer_pending || undefined,
                total: editFormData.total ? parseFloat(editFormData.total) : undefined,
                withdraw: editFormData.withdraw ? parseFloat(editFormData.withdraw) : undefined,
                earned: editFormData.earned ? parseFloat(editFormData.earned) : undefined,
            }).unwrap();

            window.alert("Данные инвестиции обновлены успешно");
            handleEditModalClose();
            refetch();
        } catch (error: any) {
            window.alert(
                error?.data?.message ||
                error?.message ||
                "Ошибка при обновлении данных инвестиции"
            );
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Инвестиции пользователей
                </h1>

                {investmentsResponse?.global_has_pending && (
                    <div className="mb-6 p-4 bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500 text-yellow-700 dark:text-yellow-200 rounded-r-md shadow-sm">
                        <div className="flex items-start">
                            <span className="text-2xl mr-3">🔔</span>
                            <div>
                                <p className="font-bold">Новые инвестиции в ожидании!</p>
                                <div className="mt-2">
                                    {investmentsResponse.pending_by_strategy && Object.entries(investmentsResponse.pending_by_strategy).map(([strategy, data]: [string, any]) => (
                                        <div key={strategy} className="grid grid-cols-[1fr_40px_100px] gap-2 justify-between items-center text-sm border-b border-yellow-400 dark:border-yellow-800">
                                            <span className="font-medium text-yellow-800 dark:text-yellow-100">Стратегия {getStrategyName(parseInt(strategy))}:</span>
                                            <span className="ml-1.5 bg-green-300 text-black px-2 py-0.5 rounded-full text-xs font-normal leading-none text-center">
                                                {data.count}
                                            </span>

                                            <span className="ml-1.5 bg-yellow-300 text-black px-2 py-0.5 rounded-full text-xs font-bold leading-none">
                                                {data.sum} USDT
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-3 text-xs opacity-75 italic border-t border-yellow-200 dark:border-yellow-800 pt-2">
                                    Пожалуйста, выберите соответствующую стратегию в фильтре выше, чтобы увидеть список пользователей и подтвердить их инвестиции.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Фильтры */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Стратегия
                        </label>
                        <select
                            value={selectedStrategy}
                            onChange={(e) =>
                                handleStrategyChange(parseInt(e.target.value))
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            {strategies.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.id}: {s.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Пользователь
                        </label>
                        <UserAutocomplete
                            value={userId}
                            onChange={handleUserIdChange}
                            onUserSelect={handleUserSelect}
                            placeholder="ID, email, phone, name..."
                            className="shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={() => refetch()}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        >
                            Обновить
                        </button>
                    </div>
                </div>

                {/* Статистика */}
                {meta && (
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            Всего записей: <span className="font-semibold">{meta.total_count}</span>
                            {" | "}
                            Стратегия: <span className="font-semibold">{getStrategyName(meta.strategy)}</span>
                            {" | "}
                            Сортировка: <span className="font-semibold">
                                {sortBy === "user_id" ? "ID пользователя" :
                                 sortBy === "total" ? "Сумма инвестирования" :
                                 sortBy === "money_active" ? "Активные средства" :
                                 sortBy === "money_pending" ? "В ожидании" :
                                 sortBy === "available" ? "Доступная для вывода" :
                                 sortBy === "earned" ? "Заработано" :
                                 sortBy === "withdraw" ? "Выведено" :
                                 sortBy === "total_earned" ? "Всего заработано" : "Не выбрано"} (
                                {sortOrder === "asc" ? "по возрастанию" : "по убыванию"})
                            </span>
                        </div>
                    </div>
                )}

                {/* Таблица */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : investments.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        Нет данных для отображения
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("user_id")}
                                    >
                                        ID
                                        {sortBy === "user_id" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("total")}
                                    >
                                        Сумма инвестирования
                                        {sortBy === "total" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("money_active")}
                                    >
                                        Активные средства
                                        {sortBy === "money_active" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("money_pending")}
                                    >
                                        В ожидании
                                        {sortBy === "money_pending" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("available")}
                                    >
                                        Доступно для вывода
                                        {sortBy === "available" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("earned")}
                                    >
                                        Заработано
                                        {sortBy === "earned" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("withdraw")}
                                    >
                                        Выведено
                                        {sortBy === "withdraw" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort("total_earned")}
                                    >
                                        Всего заработано
                                        {sortBy === "total_earned" && (
                                            <span className="ml-1">
                                                {sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {investments.map((investment) => (
                                    <tr
                                        key={`${investment.user_id}-${investment.strategy}`}
                                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                            investment.money_pending > 0 
                                                ? 'bg-yellow-100 dark:bg-yellow-900/20' 
                                                : ''
                                        }`}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {investment.user_id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {investment.user_name} {investment.user_surname}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                            {formatCurrency(investment.total_invested)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatCurrency(investment.money_active)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatCurrency(investment.money_pending)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400">
                                            {formatCurrency(investment.available_for_withdraw)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatCurrency(investment.earned)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatCurrency(investment.withdraw)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-400">
                                            {formatCurrency(investment.total_earned)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEditClick(investment)}
                                                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                                    title="Редактировать"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={() => handleWithdrawClick(investment)}
                                                    disabled={
                                                        isWithdrawing ||
                                                        withdrawingUsers.has(
                                                            `${investment.user_id}-${investment.strategy}`
                                                        ) ||
                                                        investment.available_for_withdraw <= 0
                                                    }
                                                    className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {withdrawingUsers.has(
                                                        `${investment.user_id}-${investment.strategy}`
                                                    )
                                                        ? "Вывод..."
                                                        : "Вывод"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Модалка вывода средств */}
            {isWithdrawModalOpen && selectedInvestment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Вывод средств
                            </h3>
                            <button
                                onClick={handleWithdrawModalClose}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    Пользователь: <span className="font-semibold text-gray-900 dark:text-white">{selectedInvestment.user_name} {selectedInvestment.user_surname}</span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    Стратегия: <span className="font-semibold text-gray-900 dark:text-white">{getStrategyName(selectedInvestment.strategy)}</span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Доступно для вывода: <span className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(selectedInvestment.available_for_withdraw)}</span>
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Сумма для вывода
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedInvestment.available_for_withdraw}
                                    value={withdrawAmount}
                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                    placeholder="Введите сумму"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Максимум: {formatCurrency(selectedInvestment.available_for_withdraw)}
                                </p>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={handleWithdrawModalClose}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                                >
                                    Отмена
                                </button>
                                <button
                                    onClick={handleWithdrawSubmit}
                                    disabled={
                                        isWithdrawing ||
                                        withdrawingUsers.has(
                                            `${selectedInvestment.user_id}-${selectedInvestment.strategy}`
                                        ) ||
                                        !withdrawAmount ||
                                        parseFloat(withdrawAmount) <= 0 ||
                                        parseFloat(withdrawAmount) > selectedInvestment.available_for_withdraw
                                    }
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                >
                                    {withdrawingUsers.has(
                                        `${selectedInvestment.user_id}-${selectedInvestment.strategy}`
                                    )
                                        ? "Вывод..."
                                        : "Вывести"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Модалка редактирования инвестиции */}
            {isEditModalOpen && editingInvestment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Редактирование инвестиции
                            </h3>
                            <button
                                onClick={handleEditModalClose}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    Пользователь: <span className="font-semibold text-gray-900 dark:text-white">{editingInvestment.user_name} {editingInvestment.user_surname}</span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Стратегия: <span className="font-semibold text-gray-900 dark:text-white">{getStrategyName(editingInvestment.strategy)}</span>
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Активные средства (money_active)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editFormData.money_active}
                                        onChange={(e) => setEditFormData({ ...editFormData, money_active: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        В ожидании (money_pending)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editFormData.money_pending}
                                        onChange={(e) => setEditFormData({ ...editFormData, money_pending: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        В ожидании вывода (money_withdraw_pending)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editFormData.money_withdraw_pending}
                                        onChange={(e) => setEditFormData({ ...editFormData, money_withdraw_pending: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        В ожидании перевода (money_transfer_pending)
                                    </label>
                                    <input
                                        type="text"
                                        value={editFormData.money_transfer_pending}
                                        onChange={(e) => setEditFormData({ ...editFormData, money_transfer_pending: e.target.value })}
                                        placeholder="Например: 100|2,50|3"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Общая сумма инвестирования (total)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editFormData.total}
                                        onChange={(e) => setEditFormData({ ...editFormData, total: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Выведено (withdraw)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editFormData.withdraw}
                                        onChange={(e) => setEditFormData({ ...editFormData, withdraw: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Заработано (earned)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editFormData.earned}
                                        onChange={(e) => setEditFormData({ ...editFormData, earned: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={handleEditModalClose}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                                >
                                    Отмена
                                </button>
                                <button
                                    onClick={handleEditSubmit}
                                    disabled={isUpdating}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isUpdating ? "Сохранение..." : "Сохранить"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

